import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Loader2, Gauge, AlertTriangle, CheckCircle2, TrendingUp } from "lucide-react";

const scoreColor = (s, soglia) => (s >= 90 ? "#10b981" : s >= 70 ? "#84cc16" : s >= soglia ? "#f59e0b" : "#ef4444");

export default function QualitaCRM() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    api.get("/quality/stats")
      .then((r) => { setData(r.data); setSel(r.data.items?.[0]?.commerciale || null); })
      .catch(() => setData({ items: [], soglia: 50 }));
  }, []);

  if (!data) return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const soglia = data.soglia;
  const selItem = data.items.find((i) => i.commerciale === sel);
  const totals = data.items.reduce((a, i) => ({ att: a.att + i.attivita, ins: a.ins + i.insufficienti, comp: a.comp + i.complete }), { att: 0, ins: 0, comp: 0 });

  return (
    <div className="p-4 lg:p-8 space-y-6" data-testid="qualita-crm-page">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><Gauge className="h-6 w-6" style={{ color: "#FDD003" }} /> Qualità CRM</h1>
        <p className="text-sm text-muted-foreground">Qualità delle descrizioni delle attività registrate nel CRM. Soglia minima: {soglia}/100.</p>
      </div>

      {data.items.length === 0 ? (
        <Card className="rounded border-border p-10 text-center text-sm text-muted-foreground">Nessuna attività valutata finora. Le valutazioni compaiono man mano che vengono registrate nuove attività.</Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KpiCard icon={TrendingUp} label="Attività valutate" value={totals.att} />
            <KpiCard icon={CheckCircle2} label="Complete (≥ 70)" value={totals.comp} tone="text-emerald-500" />
            <KpiCard icon={AlertTriangle} label="Insufficienti (< soglia)" value={totals.ins} tone="text-red-500" />
          </div>

          <Card className="rounded border-border overflow-x-auto" data-testid="qualita-table">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-xs uppercase tracking-wider">Commerciale</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Qualità media</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Attività</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Complete</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Insufficienti</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((i) => (
                  <TableRow key={i.commerciale} onClick={() => setSel(i.commerciale)} data-testid={`qualita-row-${i.commerciale}`} className={`cursor-pointer border-border ${sel === i.commerciale ? "bg-secondary/50" : ""}`}>
                    <TableCell className="font-medium">{i.commerciale}</TableCell>
                    <TableCell className="text-right"><span className="font-heading font-bold" style={{ color: scoreColor(i.media, soglia) }}>{i.media}</span><span className="text-muted-foreground text-xs">/100</span></TableCell>
                    <TableCell className="text-right">{i.attivita}</TableCell>
                    <TableCell className="text-right text-emerald-500">{i.complete}</TableCell>
                    <TableCell className="text-right text-red-500">{i.insufficienti}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {selItem && selItem.trend.length > 0 && (
            <Card className="rounded border-border p-5" data-testid="qualita-trend">
              <h3 className="font-heading font-semibold text-sm mb-4">Trend mensile — {selItem.commerciale}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={selItem.trend} margin={{ top: 5, right: 20, bottom: 5, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                  <XAxis dataKey="mese" stroke="#888" fontSize={11} />
                  <YAxis domain={[0, 100]} stroke="#888" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="media" stroke="#FDD003" strokeWidth={2} dot={{ r: 3 }} name="Qualità media" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone }) {
  return (
    <Card className="rounded border-border p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-1"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`font-heading font-bold text-2xl ${tone || ""}`}>{value}</div>
    </Card>
  );
}
